import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Tag, ArrowRight } from 'lucide-react';
import AltHeader from '../../components/AltHeader';
import AltFooter from '../../components/AltFooter';
import AltParallaxBackdrop from '../../components/AltParallaxBackdrop';
import blogArticles from '../../data/blogArticles';
import { getArticleImage, getResponsiveSrc } from '../../data/categoryImages';

const BlogIndex = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = useMemo(
    () => ['All', ...new Set(blogArticles.map((a) => a.category))],
    []
  );

  const filteredArticles = useMemo(
    () =>
      blogArticles.filter((article) => {
        const q = searchTerm.toLowerCase();
        const matchesSearch =
          !q ||
          article.title.toLowerCase().includes(q) ||
          article.description.toLowerCase().includes(q);
        const matchesCategory =
          selectedCategory === 'All' || article.category === selectedCategory;
        return matchesSearch && matchesCategory;
      }),
    [searchTerm, selectedCategory]
  );

  const featured = filteredArticles[0];
  const rest = filteredArticles.slice(1);

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-16 md:pb-20 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="blog-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            The Levanta Blog
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            The library of{' '}
            <em className="italic font-light text-blue-600">structured growth.</em>
          </h1>
          <p className="mt-7 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Long-form essays on discipline, habit design and the science of skill —
            written to be read once and used forever.
          </p>

          {/* Search */}
          <div className="mt-9 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0b1f3a]/40" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search articles…"
                aria-label="Search articles"
                data-testid="blog-search"
                className="w-full pl-14 pr-5 py-4 rounded-full bg-white/95 backdrop-blur-sm text-[#0b1f3a] placeholder:text-[#0b1f3a]/40 shadow-lg ring-1 ring-black/5 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY FILTERS */}
      <section className="px-6 pt-10 md:pt-14 pb-2 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="flex gap-2 flex-wrap justify-center"
            role="tablist"
            aria-label="Filter by category"
          >
            {categories.map((category) => {
              const isActive = selectedCategory === category;
              return (
                <button
                  key={category}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`blog-category-${category}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold ring-1 transition-all duration-200 ${
                    isActive
                      ? 'bg-[#0b1f3a] text-white ring-[#0b1f3a] shadow-md'
                      : 'bg-white text-[#0b1f3a]/75 ring-black/5 hover:ring-blue-300 hover:text-[#0b1f3a] hover:-translate-y-0.5'
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          <p
            className="text-sm text-[#0b1f3a]/50 mt-6 text-center"
            data-testid="blog-result-count"
          >
            {filteredArticles.length}{' '}
            {filteredArticles.length === 1 ? 'article' : 'articles'}
            {selectedCategory !== 'All' && ` in ${selectedCategory}`}
          </p>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="px-6 pt-10 pb-14 md:pb-24 bg-white">
        <div className="max-w-6xl mx-auto">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-20 px-6 rounded-3xl border-2 border-dashed border-[#0b1f3a]/15 bg-[#fafbfd]">
              <p className="text-lg text-[#0b1f3a]/70">
                No articles match{' '}
                <span className="font-semibold">&ldquo;{searchTerm}&rdquo;</span>.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('All');
                }}
                className="mt-5 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {/* Featured (first) — full-width on desktop */}
              {featured && selectedCategory === 'All' && !searchTerm && (
                <Link
                  to={`/blog/${featured.slug}`}
                  data-testid={`blog-featured-${featured.slug}`}
                  className="group grid md:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20 items-stretch"
                >
                  <div className="relative aspect-[4/3] md:aspect-auto rounded-3xl overflow-hidden bg-[#fafbfd] ring-1 ring-black/5 shadow-sm">
                    {(() => {
                      const r = getResponsiveSrc(getArticleImage(featured), {
                        sizes: '(max-width: 768px) 92vw, 50vw',
                      });
                      return (
                        <img
                          src={r?.src}
                          srcSet={r?.srcSet}
                          sizes={r?.sizes}
                          alt={featured.title}
                          loading="eager"
                          width="1200"
                          height="675"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      );
                    })()}
                  </div>
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">
                        Featured
                      </span>
                      <span className="h-px w-12 bg-blue-300" />
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0b1f3a]/60">
                        <Tag className="w-3 h-3" />
                        {featured.category}
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black leading-[1.1] tracking-tight text-[#0b1f3a] group-hover:text-blue-600 transition-colors duration-200">
                      {featured.title}
                    </h2>
                    <p className="mt-5 text-lg text-[#0b1f3a]/70 leading-relaxed line-clamp-3">
                      {featured.description}
                    </p>
                    <span className="mt-7 inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-3 transition-all duration-200">
                      Read the article
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              )}

              {/* Article grid */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {(selectedCategory === 'All' && !searchTerm ? rest : filteredArticles).map(
                  (article) => (
                    <Link
                      key={article.id}
                      to={`/blog/${article.slug}`}
                      data-testid={`blog-article-${article.slug}`}
                      className="group flex flex-col rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:ring-blue-200"
                    >
                      <div className="aspect-[16/10] overflow-hidden bg-[#fafbfd]">
                        {(() => {
                          const r = getResponsiveSrc(getArticleImage(article));
                          return (
                            <img
                              src={r?.src}
                              srcSet={r?.srcSet}
                              sizes={r?.sizes}
                              alt={article.title}
                              loading="lazy"
                              width="1200"
                              height="675"
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          );
                        })()}
                      </div>
                      <div className="p-6 md:p-7 flex flex-col flex-1">
                        <span className="inline-flex self-start items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-600 mb-3">
                          <Tag className="w-3 h-3" />
                          {article.category}
                        </span>
                        <h2 className="text-xl md:text-2xl font-black text-[#0b1f3a] leading-tight mb-3 group-hover:text-blue-600 transition-colors duration-200">
                          {article.title}
                        </h2>
                        <p className="text-[#0b1f3a]/65 text-[15px] leading-relaxed line-clamp-3 mb-5">
                          {article.description}
                        </p>
                        <span className="mt-auto inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                          Read more
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </Link>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Reading is{' '}
            <em className="italic font-light text-blue-300">step one.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            Levanta turns the ideas into a daily structure you actually return to.
          </p>
          <div className="mt-10">
            <Link
              to="/download-app"
              data-testid="blog-cta-download"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Download Levanta
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default BlogIndex;
